import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import PortalHeader from '../../components/Portal/PortalHeader';
import {
	subscribeNotifications,
	setNotificationRead,
	markAllRead,
	groupByDay,
	timeLabel,
	toDate,
	typeMeta,
} from '../../services/notificationService';
import '../../components/Portal/portalSurface.css';
import './NotificationsPage.css';

/* Notifications — the persisted record of every staff push (card
   VVo3vlre9wI4gUa1KTzI, 2026-09-22).

   Anthony, on why: "I thought Dani only canceled last week. I didn't realize
   she canceled this week as well. This is why I need to build a notifications
   page or section, because I need to be able to track when all of these things
   happen." Two cancellations 31 seconds apart arrived as two pushes that read
   as one, and nothing kept either. Here each is its own row, naming the lesson
   date in its body, kept for 90 days (expiresAt + a Firestore TTL policy).

   Rows are the user's OWN (the rule and the query both say uid == me). Tapping
   a row marks it read and goes to its link; the separate Mark read / Mark
   unread control is always rendered (never hover-only: keratoconus, and the
   PWA has no hover), so a row can be put back to unread as a reminder. Unread
   is signalled by weight, an accent bar AND a "New" label, never colour alone. */

const FILTERS = [
	{ key: 'all', label: 'All' },
	{ key: 'unread', label: 'Unread' },
];

function NotificationsPage() {
	const { currentUser } = useAuth();
	const uid = currentUser?.uid || null;
	const navigate = useNavigate();
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [filter, setFilter] = useState('all');
	const [busy, setBusy] = useState(null);

	useEffect(() => {
		if (!uid) {
			setLoading(false);
			return undefined;
		}
		const unsub = subscribeNotifications(
			uid,
			(next) => {
				setRows(next);
				setLoading(false);
				setError('');
			},
			(err) => {
				console.error('Notifications failed to load:', err);
				setError("We couldn't load your notifications just now. Please try again in a moment.");
				setLoading(false);
			}
		);
		return () => unsub && unsub();
	}, [uid]);

	const unreadIds = useMemo(() => rows.filter((r) => !r.readAt).map((r) => r.id), [rows]);
	const shown = filter === 'unread' ? rows.filter((r) => !r.readAt) : rows;
	const groups = groupByDay(shown);

	const toggleRead = async (row) => {
		setBusy(row.id);
		try {
			await setNotificationRead(row.id, !row.readAt);
		} catch (e) {
			console.error('Could not update notification', e);
			setError("That change didn't save. Please try again.");
		} finally {
			setBusy(null);
		}
	};

	const openRow = async (row) => {
		if (!row.readAt) {
			// Fire and forget: the navigation must not wait on the write, and the
			// live listener reflects it when it lands.
			setNotificationRead(row.id, true).catch((e) => console.error('Could not mark read', e));
		}
		if (row.link) navigate(row.link);
	};

	const readAll = async () => {
		setBusy('all');
		try {
			await markAllRead(unreadIds);
		} catch (e) {
			console.error('Could not mark all read', e);
			setError("That change didn't save. Please try again.");
		} finally {
			setBusy(null);
		}
	};

	return (
		<div className='ps-page ntf-page'>
			<PortalHeader title='Notifications' subtitle='Everything the studio sent you, newest first. Kept for 90 days.' />

			<div className='ntf-toolbar'>
				<div className='ps-tabs ntf-tabs' role='tablist' aria-label='Show'>
					{FILTERS.map((f) => (
						<button
							key={f.key}
							type='button'
							role='tab'
							aria-selected={filter === f.key}
							className={`ps-tab${filter === f.key ? ' is-active' : ''}`}
							onClick={() => setFilter(f.key)}>
							{f.label}
							{f.key === 'unread' && unreadIds.length > 0 && ` (${unreadIds.length})`}
						</button>
					))}
				</div>
				<button
					type='button'
					className='ps-btn ntf-readall'
					onClick={readAll}
					disabled={!unreadIds.length || busy === 'all'}>
					<i className='fa-sharp fa-solid fa-check-double' aria-hidden='true' /> Mark all read
				</button>
			</div>

			{error && (
				<div className='ps-alert ps-alert--danger' role='alert'>
					{error}
				</div>
			)}

			{loading ? (
				<div className='ps-empty'>
					<i className='fa-sharp fa-solid fa-spinner fa-spin' aria-hidden='true' /> Loading…
				</div>
			) : error && rows.length === 0 ? null : shown.length === 0 ? (
				/* An empty state is a CLAIM ("nothing happened"); after a failed
				   load the page does not know that, so it shows only the error. */
				<div className='ps-empty'>
					<i className='fa-sharp fa-solid fa-bell' aria-hidden='true' />
					<p>
						{filter === 'unread'
							? "You're all caught up."
							: 'No notifications yet. Cancellations, makeups, registrations, tasks and mail alerts will show up here.'}
					</p>
				</div>
			) : (
				groups.map((g) => (
					<section key={g.label} className='ntf-day'>
						<h2 className='ntf-day__label'>{g.label}</h2>
						<ul className='ntf-list'>
							{g.rows.map((row) => {
								const meta = typeMeta(row.type);
								const unread = !row.readAt;
								const at = toDate(row.createdAt);
								return (
									<li key={row.id} className={`ntf-row${unread ? ' is-unread' : ''}`}>
										<button
											type='button'
											className='ntf-row__main'
											onClick={() => openRow(row)}
											title={row.link ? 'Open' : undefined}>
											<span className={`ntf-row__icon ntf-row__icon--${row.type || 'other'}`} aria-hidden='true'>
												<i className={`fa-sharp fa-solid ${meta.icon}`} />
											</span>
											<span className='ntf-row__text'>
												<span className='ntf-row__title'>
													{row.title}
													{unread && <span className='ntf-row__new'>New</span>}
												</span>
												{row.body && <span className='ntf-row__body'>{row.body}</span>}
												<span className='ntf-row__meta'>
													{timeLabel(at)} · {meta.label}
												</span>
											</span>
										</button>
										<button
											type='button'
											className='ntf-row__toggle'
											onClick={() => toggleRead(row)}
											disabled={busy === row.id}
											aria-label={unread ? `Mark "${row.title}" read` : `Mark "${row.title}" unread`}>
											{unread ? 'Mark read' : 'Mark unread'}
										</button>
									</li>
								);
							})}
						</ul>
					</section>
				))
			)}
		</div>
	);
}

export default NotificationsPage;
